import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { useSeoMeta } from '../api';

function setMeta(selector: string, attr: 'name' | 'property', key: string, content: string | undefined) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!content) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.content = content;
}

/**
 * Keeps title, description, canonical, robots, Open Graph and JSON-LD in sync with the
 * current public route — the same data the server injects for crawlers.
 */
export function SeoHead() {
  const { pathname } = useLocation();
  const { data } = useSeoMeta(pathname);
  useEffect(() => {
    if (!data) return;
    document.title = data.title;
    setMeta('meta[name="description"]', 'name', 'description', data.description);
    setMeta('meta[name="robots"]', 'name', 'robots', data.robots);
    setMeta('meta[property="og:title"]', 'property', 'og:title', data.title);
    setMeta('meta[property="og:description"]', 'property', 'og:description', data.description);
    setMeta('meta[property="og:image"]', 'property', 'og:image', data.image);
    let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = data.canonical;
    document.head.querySelectorAll('script[data-seo="jsonld"]').forEach((s) => s.remove());
    for (const j of data.jsonLd) {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.dataset.seo = 'jsonld';
      s.textContent = JSON.stringify(j);
      document.head.appendChild(s);
    }
  }, [data]);
  return null;
}
